import React, { useEffect, useRef, useState } from 'react';

const ThemePreviewModal = ({ isOpen, onClose, themeId, themeName }) => {
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
    }
  }, [isOpen, themeId]);

  useEffect(() => {
    if (isOpen && iframeRef.current) {
      const iframe = iframeRef.current;
      
      // Handle iframe load - just mark loading as complete
      // Theme application is handled by Home.js via URL params
      const handleLoad = () => {
        // Wait a bit for the theme to be applied in the iframe
        setTimeout(() => {
          setIsLoading(false);
        }, 500);
      };

      iframe.addEventListener('load', handleLoad);
      
      return () => {
        iframe.removeEventListener('load', handleLoad);
      };
    }
  }, [isOpen, themeId]);

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.3s ease-out'
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'relative',
          width: '95%',
          maxWidth: '1400px',
          height: '90vh',
          backgroundColor: 'var(--modal-bg)',
          borderRadius: '20px',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
          border: '2px solid var(--border-color)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideInUp 0.4s ease-out'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 30px',
            borderBottom: '2px solid var(--border-color)',
            backgroundColor: 'var(--card-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontSize: '24px',
                color: 'var(--text-primary)',
                fontWeight: 'bold'
              }}
            >
              🎨 Theme Preview: {themeName}
            </h2>
            <p
              style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: 'var(--text-secondary)'
              }}
            >
              This is how the home page would look with this theme
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              border: '2px solid var(--border-color)',
              backgroundColor: 'var(--card-bg)',
              color: 'var(--text-primary)',
              fontSize: '24px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.3s ease',
              flexShrink: 0
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--error-bg)';
              e.target.style.color = 'var(--error-text)';
              e.target.style.transform = 'rotate(90deg)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'var(--card-bg)';
              e.target.style.color = 'var(--text-primary)';
              e.target.style.transform = 'rotate(0deg)';
            }}
          >
            ✕
          </button>
        </div>

        {/* Preview Frame */}
        <div
          style={{
            flex: 1,
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#000'
          }}
        >
          {isLoading && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'var(--bg-primary)',
                zIndex: 1
              }}
            >
              <div
                style={{
                  fontSize: '20px',
                  color: 'var(--text-primary)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '16px'
                }}
              >
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    border: '4px solid var(--border-color)',
                    borderTop: '4px solid var(--btn-primary)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                <div>Loading preview...</div>
              </div>
            </div>
          )}
          <iframe
            key={themeId}
            ref={iframeRef}
            src={`${window.location.origin}/?preview=${themeId}&hideNav=true&t=${Date.now()}`}
            title={`${themeName} Preview`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
              opacity: isLoading ? 0 : 1,
              transition: 'opacity 0.3s ease'
            }}
            sandbox="allow-same-origin allow-scripts"
          />
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 30px',
            borderTop: '2px solid var(--border-color)',
            backgroundColor: 'var(--card-bg)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0
          }}
        >
          <div
            style={{
              fontSize: '14px',
              color: 'var(--text-secondary)'
            }}
          >
            💡 Tip: Scroll in the preview to see more of the theme
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px',
              fontSize: '16px',
              fontWeight: 'bold',
              borderRadius: '8px',
              border: 'none',
              cursor: 'pointer',
              backgroundColor: 'var(--btn-primary)',
              color: 'var(--text-light)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = 'none';
            }}
          >
            Close Preview
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export default ThemePreviewModal;
